import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import RankName from '../RankName';
import CountUp from '../CountUp';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folie 32 — Worldpools.
 * Two blocks:
 *  1) Three pool eligibility cards (Pool 1 / 2 / 3) with qualification rank.
 *  2) Rank matrix: 7 rank columns (Blue Diamond → Black Diamond) with 3 pool
 *     indicator dots per rank. Active pools glow gold; inactive stay dim.
 *     Provides an instant, unambiguous read of who participates in which pool.
 */
export default function SectionWorldPools() {
  const { t } = useT();
  const m = t.worldPools;

  return (
    <SectionShell id="world-pools" className="">
      <div className="grid grid-cols-12 gap-y-10 md:gap-12 mb-12 items-end">
        <div className="col-span-12 md:col-span-4">
          <Reveal>
            <div className="overline mb-6">{m.overline}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white max-w-[16ch]">
              {m.title}
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Reveal delay={120}>
            <p className="font-ui text-3xl md:text-4xl lg:text-[2.6rem] text-white/85 leading-snug mb-4" style={{ whiteSpace: 'pre-line' }}>
              {m.sub}
            </p>
            <p className="font-ui text-sm text-[#f2d979]/85 uppercase tracking-[0.16em]">
              {m.forever}
            </p>
          </Reveal>
        </div>
      </div>

      {/* Three pool eligibility cards */}
      <div className="wp-pool-row mb-14" data-testid="wp-pools">
        {m.pools.map((p, i) => (
          <Reveal key={p.key} delay={140 + i * 100}>
            <div className="wp-pool-card" data-testid={`wp-pool-${i + 1}`} style={{ '--step': 2 }}>
              <div className="wp-pool-card-inner">
                <div className="wp-pool-index font-display">{`0${i + 1}`}</div>
                <div className="wp-pool-body">
                  <div className="wp-pool-label font-display">{p.label}</div>
                  <div className="font-ui text-xs text-white/55 mt-1">{p.qual}</div>
                </div>
                <div className="wp-pool-value font-display">{p.value}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Rank matrix — 7 columns, each with coin + 3 pool dots */}
      <Reveal delay={480}>
        <div className="wp-matrix-scroll">
          <div className="wp-matrix" data-testid="wp-matrix">
            {m.ranks.map((r, idx) => {
              const totalShares = r.shares.reduce((s, n) => s + n, 0);
              return (
                <div
                  key={r.name}
                  className="wp-rank-card"
                  data-testid={`wp-rank-${idx}`}
                  style={{ '--step': idx, '--active-count': totalShares }}
                >
                  <div className="wp-coin">
                    <img
                      src={`/source-pptx/${r.coin}`}
                      alt={`${r.name} Rang`}
                      loading="lazy"
                      draggable={false}
                      className="block w-full h-full object-contain select-none"
                      style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.6))' }}
                    />
                  </div>

                  <div className="wp-rank-name font-display"><RankName name={r.name} /></div>

                  {/* 3 pool rows, each showing number of shares */}
                  <div className="wp-share-list">
                    {r.shares.map((count, i) => {
                      const isActive = count > 0;
                      return (
                        <div
                          key={i}
                          className={`wp-share-row ${isActive ? 'wp-share-row--active' : 'wp-share-row--inactive'}`}
                          data-testid={`wp-share-${idx}-${i + 1}`}
                        >
                          <span className="wp-share-label">Pool {i + 1}</span>
                          <span className="wp-share-count font-display">
                            {isActive ? `×${count}` : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="wp-active-label font-ui">
                    {totalShares} {totalShares === 1 ? 'Anteil' : 'Anteile'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Big total */}
      <Reveal delay={860}>
        <div className="unilevel-total mt-12" data-testid="wp-total">
          <span className="unilevel-total-line" aria-hidden />
          <div className="unilevel-total-block">
            <span className="unilevel-total-big font-display">{m.totalPrefix} </span>
            <span className="unilevel-total-huge font-display"><CountUp value={m.totalValue} /></span>
            <span className="unilevel-total-big font-display"> {m.totalSuffix}</span>
          </div>
          <span className="unilevel-total-line" aria-hidden />
        </div>
      </Reveal>
    </SectionShell>
  );
}
